import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ContratosTableRow,
  headersContratosCustomTable,
  valoresExibidosOffCanvasContratos,
} from './auxiliarContratos';
import BarraInferiorBotoes from '../../../../components/BarraInferiorBotoes';
import BarraSubAbas from '../../../../components/BarraSubAbas';
import BoxConteudo from '../../../../components/BoxConteudo';
import ContainerConteudo from '../../../../components/ContainerConteudo';
import CustomCol from '../../../../components/CustomCol';
import { CustomSwal } from '../../../../components/CustomFeedback/CustomSwal';
import CustomOffcanvas from '../../../../components/CustomOffcanvas';
import CustomRow from '../../../../components/CustomRow';
import DetailsTable from '../../../../components/CustomTable/DetailsTable';
import CustomTableOffcanvas from '../../../../components/CustomTableOffcanvas';
import Input from '../../../../components/InputNew';
import Select from '../../../../components/InputNew/Select';
import WorkspaceBotoes from '../../../../components/WorkspaceBotoes';
import WorkspaceFicha from '../../../../components/WorkspaceFicha';
import { getUser } from '../../../../modules/contexts/Auth/RequireAuth';
import { useSelect } from '../../../../modules/contexts/Select/useSelect';
import { getTipoEnvolvimentoOptions } from '../../../../modules/types/TipoEnvolvimentoEnum';
import {
  getArp,
  getArpsDisponiveisByUsuario,
} from '../../../../services/contratos/arpsService';
import { getContratosByUsuario } from '../../../../services/contratos/contratosService';
import { getAllFornecedores } from '../../../../services/fornecedoresDAF/fornecedorService';
import { isHttpError } from '../../../../services/httpClient';
import {
  getProaq,
  getProaqsByUsuario,
} from '../../../../services/processosAquisitivos/proaqService';
import {
  formataNomeMeioSiglas,
  formatDate,
  formatDateSimp,
} from '../../../../utils/functions';
import { SubAbas as subAbas } from '../../ProcessosAquisitivos/subabas';

const ContratosIndex: React.FC = () => {
  //DADOS USUARIO
  const user = getUser();
  const userUnidadeDaf = user.unidade;
  const userComponenteAFLabel =
    user?.vinculo?.[0]?.nomeInstituicao ?? 'Não informado';

  const navigate = useNavigate();

  // GETS
  const { getSelectDominioPai } = useSelect();
  const statusContratoOptions = getSelectDominioPai(52);
  const leiLicitacaoOptions = getSelectDominioPai(65);
  const componenteAfOptions = getSelectDominioPai(1);
  const modalidadeAquisicaoOptions = getSelectDominioPai(133);
  const submodalidadeAquisicaoOptions = getSelectDominioPai(139);

  // FUNÇÕES E OUTROS

  const tituloModal = 'Novo Contrato';

  const binaryOptions = [
    { value: 0, label: 'Não informado' },
    { value: 1, label: 'Sim' },
    { value: 2, label: 'Não' },
  ];

  //Dados do componente
  const [data, setData] = useState<any[]>([]);
  const [fornecedoresData, setFornecedoresData] = useState<any[]>([]);
  const [arpsData, setArpsData] = useState<any[]>([]);
  const [proaqData, setProaqData] = useState<any[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [tipoBaseContrato, setTipoBaseContrato] = useState(0);
  const [statusInserirNewArp, setStatusInserirNewArp] = useState<boolean>(true);

  const [formNewContrato, setFormNewContrato] = useState({
    arpId: 0,
    arpLabel: '',
    arpItem: '',
    proaqId: 0,
    proaqLabel: '',
    proaqItem: '',
    tipoEnvolvimento: 0,
    fornecedorId: 0,
    cnpj: '',
    kitId: 0,
    denominacaoId: 0,
    componenteAf: userUnidadeDaf - 1,
    arp: null,
    proaq: null,
  });
  const [formFiltros, setFormFiltros] = useState({
    statusContrato: 0,
    componenteAf: 0,
    denominacaoGenerica: '',
    nContrato: 0,
  });

  const fornecedoresOptions = [
    { value: 0, label: 'Não informado' },
    ...fornecedoresData.map((item) => ({
      value: item.id,
      label: item.nomeFantasia,
    })),
  ];

  // Buscar dados
  const fetchProaqs = async () => {
    try {
      const response = await getProaqsByUsuario();

      // Filtra os registros
      const filtrados = response.filter((item: any) =>
        [141, 138].includes(Number(item.submodalidade)),
      );

      // Mapeia para options do Select
      const options = filtrados.map((item: any) => ({
        value: item.id,
        label:
          'ID: ' +
          item.id +
          ' - NUP: ' +
          item.numeroSeiProaq +
          ' - ' +
          (item.denominacaoGenerica ? '(D)' : '(K)') +
          ' ' +
          (item.denominacaoGenerica?.denominacaoGenerica ||
            item.KitProdutoDAF?.nomeKit) +
          ' (' +
          (item.denominacaoGenerica?.id || item.KitProdutoDAF?.id) +
          ')',
      }));

      // Atualiza o state
      setProaqData([{ value: 0, label: 'Não informado' }, ...options]);
    } catch (error) {
      console.error('Erro ao buscar processos aquisitivos:', error);
    }
  };

  const fetchArps = async () => {
    try {
      const response = await getArpsDisponiveisByUsuario();
      // Filtra os registros
      const filtrados = response.filter(
        (item: any) => item.proaq.submodalidade === 140,
      );

      // Mapeia para options do Select
      const options = filtrados.map((arp: any) => ({
        value: arp.id,
        label:
          'ID: ' +
          arp.id +
          ' - nº: ' +
          arp.numeroArp +
          ' - ' +
          (arp.denominacaoGenerica ? '(D)' : '(K)') +
          ' ' +
          (arp.denominacaoGenerica?.denominacaoGenerica ||
            arp.kitProdutoDAF?.nomeKit ||
            'Não informado') +
          ' (' +
          (arp.denominacaoGenerica?.id || arp.kitProdutoDAF?.id || 0) +
          ')',
      }));

      // Atualiza o state
      setArpsData([{ value: 0, label: 'Não informado' }, ...options]);
    } catch (error) {
      console.error('Erro ao buscar Arps:', error);
    }
  };

  const fetchFornecedores = async () => {
    try {
      const fornecedoresData = await getAllFornecedores();
      setFornecedoresData(fornecedoresData);
    } catch (error: any) {
      if (isHttpError(error)) {
        console.error(error.response?.data.message);
      }
    }
  };

  const fetchData = async () => {
    try {
      const registros = await getContratosByUsuario();
      setData(registros);
    } catch (error) {
      console.error('Erro ao obter dados:', error);
    }
  };

  const fetchArp = async (arpId: string) => {
    try {
      const arp = await getArp(arpId);

      if (arp?.fornecedor?.id) {
        setFormNewContrato((prev) => ({
          ...prev,
          fornecedorId: arp.fornecedor?.id ?? 0,
          proaqId: arp.proaq?.id ?? 0,
          arp: arp,
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar ARP:', error);
    }
  };

  const fetchProaq = async (proaqId: string) => {
    try {
      const proaq = await getProaq(proaqId);

      setFormNewContrato((prev) => ({
        ...prev,
        proaq: proaq,
      }));
    } catch (error) {
      console.error('Erro ao buscar Processo Licitatório:', error);
    }
  };

  // USE EFFECT
  useEffect(() => {
    fetchProaqs();
    fetchFornecedores();
    fetchArps();
    fetchData();
  }, []);

  useEffect(() => {
    // Verifica se as condições são satisfeitas
    if (formNewContrato.tipoEnvolvimento === 0) {
      setStatusInserirNewArp(true);
    } else {
      setStatusInserirNewArp(false);
    }
  }, [formNewContrato]);

  useEffect(() => {
    if (formNewContrato.arpId != 0) {
      fetchArp(formNewContrato.arpId.toString());
    }
  }, [formNewContrato.arpId]);

  useEffect(() => {
    if (formNewContrato.proaqId != 0) {
      fetchProaq(formNewContrato.proaqId.toString());
    }
  }, [formNewContrato.proaqId]);

  useEffect(() => {
    if (fornecedoresData) {
      const cnpj =
        fornecedoresData.find(
          (fornecedor) => fornecedor.id === formNewContrato.fornecedorId,
        )?.cnpj ?? '';
      setFormNewContrato({ ...formNewContrato, cnpj: cnpj });
    }
  }, [formNewContrato.fornecedorId]);

  // TABELA E OFFCANVAS
  const tableData = data.map((item) => ({
    id: item?.id ?? 'Não informado',

    status:
      statusContratoOptions.find(
        (status) => status.value === item.statusContrato,
      )?.label ?? 'Não informado',
    componenteAf:
      componenteAfOptions.find(
        (componenteAf) => componenteAf.value === item.componenteAf,
      )?.label ?? 'Não informado',

    modalidade:
      (modalidadeAquisicaoOptions.find(
        (option) => Number(option.value) === Number(item.proaq.modalidade),
      )?.label ?? '') +
        ' ' +
        (submodalidadeAquisicaoOptions.find(
          (option) => Number(option.value) === Number(item.proaq.submodalidade),
        )?.label ?? '') || 'Não se aplica',
    numeroContrato: item.numeroContrato ?? 'Não informado',
    fornecedor:
      '[' + item.fornecedor?.cnpj + '] - ' + item.fornecedor?.nomeFantasia,

    denominacaoKit:
      item?.denominacaoGenerica.denominacaoGenerica ??
      item?.KitProdutoDAF?.nomeKit ??
      'Não informado',
    denominacaoKitBool: item?.denominacaoGenerica ? 'D' : 'K',
  }));

  const offCanvasData = data.map((item) => {
    // Definindo cores de fundo com base no status do documento
    let statusBackgroundColor = '';
    let colorText = 'black';

    const statusLabel =
      statusContratoOptions.find(
        (option) => option.value === item.statusContrato,
      )?.label ?? 'Em Elaboração';

    if (statusLabel === 'Em Elaboração') {
      statusBackgroundColor = '#fff89a';
    } else if (statusLabel === 'Em Execução') {
      statusBackgroundColor = '#008d00a7';
      colorText = 'white';
    } else if (statusLabel === 'Encerrado') {
      statusBackgroundColor = '#757575';
    } else if (statusLabel === 'Suspenso') {
      statusBackgroundColor = '#ffc9b4dd';
    } else if (statusLabel === 'Cancelado') {
      statusBackgroundColor = '#ff8a8ac3';
    }

    // Calculando data de vigência
    let formattedDataVigencia = '';
    let diferencaEmDias = '';

    if (item.dataPublicacao) {
      const dataVigencia = new Date(item.dataPublicacao);
      dataVigencia.setDate(dataVigencia.getDate() + 365);

      formattedDataVigencia = formatDateSimp(dataVigencia.toISOString());

      const hoje = new Date();
      const dias = Math.ceil(
        (dataVigencia.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24),
      );
      diferencaEmDias = dias.toString();
    }

    return {
      id: item.id,
      dataRegistro:
        formatDate(item.dataRegistro) +
        ' - ' +
        formataNomeMeioSiglas(item.usuarioRegistro.nomeCompleto),
      dataUltimaAtualizacao:
        formatDate(item.dataUltimaAtualizacao) +
        ' - ' +
        formataNomeMeioSiglas(item.usuarioAtualizacao.nomeCompleto),

      dataUltimaVisualizacao:
        formatDate(item.dataUltimaVisualizacao) +
        ' - ' +
        formataNomeMeioSiglas(item.usuarioUltimaVisualizacao.nomeCompleto),
      nEdicoes: item.nEdicoes,
      nVisualizacoes: item.nVisualizacoes,
      separadorLog: '',
      statusContrato: (
        <span
          style={{
            backgroundColor: statusBackgroundColor,
            color: colorText,
            borderRadius: '10px',
            padding: '10px',
            display: 'inline-block',
            minWidth: '100px',
            textAlign: 'center',
            fontWeight: 'bold',
          }}
        >
          {statusLabel}
        </span>
      ),
      componenteAf:
        componenteAfOptions.find((option) => option.value === item.componenteAf)
          ?.label || 'Não informado',
      // itemAdquirido: (
      //   <b style={{ fontSize: '1.25rem' }}>{item.itemAdquirido}</b>
      // ),
      fornecedor: item.fornecedor?.nomeFantasia ?? 'Fornecedor Genérico',
      leiLicitacao:
        leiLicitacaoOptions.find((option) => option.value === item.leiLicitacao)
          ?.label || 'Não informado',
      numeroSeiContrato: item.numeroSeiContrato || 'Não informado',
      numeroContrato: item.numeroContrato || 'Não informado',
      dataPublicacao: formatDateSimp(item.dataPublicacao) || 'Não informado',
      dataVigencia: formattedDataVigencia || 'Não informado',
      prazoVigencia: diferencaEmDias || 'Não informado',
      // nItens: item.nItensArp,
      // valorTotal: formatoMoney(item.valorTotalArp.toFixed(2)),
      separadorItens: '',
      // itensAdquiridos: itensAdquiridos,
      separadorObservacoes: '',
      observacoes: item.observacoes,
    };
  });

  // FUNÇÕES HANDLE
  const handleClearModal = () => {
    setFormNewContrato({
      ...formNewContrato,
      arpId: 0,
      arpLabel: '',
      arpItem: '',
      proaqId: 0,
      proaqLabel: '',
      proaqItem: '',
      fornecedorId: 0,
      cnpj: '',
      tipoEnvolvimento: 0,
    });
    setTipoBaseContrato(0);
  };

  const handleShowModal = () => {
    handleClearModal();
    setShowModal((show) => !show);
  };

  const handleClickModalInserir = () => {
    if (
      ['Gabinete', 'Coordenação de Fiscalização de Contratos'].includes(
        userComponenteAFLabel,
      )
    ) {
      CustomSwal.show({
        type: 'warning',
        title: 'Atenção',
        html: 'Usuários vinculados ao Gabinete ou à Coordenação de Fiscalização de Contratos não podem criar Contratos.',
        showConfirmButton: true,
      });
      return;
    }
    navigate('dadosgerais', { state: { formNewContrato } });
  };

  const handleChangeModal = (e: any) => {
    const { name, value } = e.target;
    const newValue = parseInt(value, 10);

    setFormNewContrato((prev) => {
      const updatedForm = { ...prev, [name]: newValue };

      return updatedForm;
    });

    console.log('Form Novo Contrato atualizado:', {
      formNewContrato,
    });
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormFiltros((prev) => ({
      ...prev,
      [name]: parseInt(value, 10),
    }));
  };

  const [selectedDetailsRowId, setSelectedDetailsRowId] = useState<
    string | number | null
  >(null);
  const offcanvasOpenRoute = useMemo(() => {
    if (!selectedDetailsRowId) return undefined;

    const baseHash = window.location.hash.replace(/\/$/, '');
    return `${baseHash}/${selectedDetailsRowId}/dadosgerais`;
  }, [selectedDetailsRowId]);

  const handleOpenDetails = (row: ContratosTableRow) => {
    if (!row?.id) return;
    setSelectedDetailsRowId(row.id);
  };

  const handleTableRowClick = (row: ContratosTableRow) => {
    if (row?.id) {
      navigate(`${row.id}/dadosgerais`);
    }
  };

  return (
    <>
      <BarraSubAbas subAbas={subAbas} />

      <ContainerConteudo>
        <CustomTableOffcanvas
          headers={headersContratosCustomTable}
          data={tableData}
          displayValues={valoresExibidosOffCanvasContratos}
          offcanvasTitle="Detalhes do Contrato"
          widthOffcanvas="50%"
          openRoute={offcanvasOpenRoute}
          onOpenOffcanvas={handleOpenDetails}
          onRowClick={handleTableRowClick}
          itemsPerPage={50}
          showPagination={true}
          showOpenButton={true}
          filterable={true}
        >
          {(row) => (
            <DetailsTable
              rows={offCanvasData}
              selectedId={row?.id ?? selectedDetailsRowId}
              displayValues={valoresExibidosOffCanvasContratos}
            />
          )}
        </CustomTableOffcanvas>
        <CustomOffcanvas
          title={tituloModal}
          show={showModal}
          onClose={handleShowModal}
          width="70vw"
        >
          <WorkspaceFicha>
            <BoxConteudo boxTitulo="Dados do Novo Contrato">
              <CustomRow>
                <CustomCol width="220px">
                  <Select
                    name="tipoBaseContrato"
                    value={tipoBaseContrato}
                    options={binaryOptions}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                      handleClearModal();
                      setTipoBaseContrato(Number(e.target.value));
                    }}
                    label="Vincular ARP"
                  />
                </CustomCol>
                <CustomCol>
                  <Select
                    name="arpId"
                    value={formNewContrato.arpId}
                    options={arpsData}
                    onChange={handleChangeModal}
                    label="ARP"
                    disabled={tipoBaseContrato !== 1}
                  />
                </CustomCol>
              </CustomRow>
              <CustomRow>
                <CustomCol>
                  <Select
                    name="proaqId"
                    value={formNewContrato.proaqId}
                    options={proaqData}
                    onChange={handleChangeModal}
                    label="Processo Aquisitivo"
                    disabled={tipoBaseContrato !== 2}
                  />
                </CustomCol>
              </CustomRow>
              <CustomRow>
                <CustomCol width="250px">
                  <Input
                    id="cnpj"
                    name="cnpj"
                    label="CNPJ"
                    value={formNewContrato.cnpj}
                    onChange={handleChange}
                    disabled
                  />
                </CustomCol>
                <CustomCol>
                  <Select
                    name="fornecedorId"
                    value={formNewContrato.fornecedorId}
                    options={fornecedoresOptions}
                    onChange={handleChangeModal}
                    label="Fornecedor"
                    disabled={
                      formNewContrato.proaqId === 0 ||
                      (formNewContrato.proaqId !== 0 &&
                        formNewContrato.arpId !== 0)
                        ? true
                        : false
                    }
                  />
                </CustomCol>
              </CustomRow>
              <CustomRow>
                <CustomCol width="250px">
                  <Select
                    name="tipoEnvolvimento"
                    value={formNewContrato.tipoEnvolvimento}
                    options={getTipoEnvolvimentoOptions()}
                    onChange={handleChangeModal}
                    label="Papel do Fornecedor"
                    disabled={formNewContrato.fornecedorId === 0 ? true : false}
                  />
                </CustomCol>
              </CustomRow>
            </BoxConteudo>

            <BarraInferiorBotoes>
              <WorkspaceBotoes
                inserir
                inserirDisabled={statusInserirNewArp}
                onClickInserir={handleClickModalInserir}
              />
            </BarraInferiorBotoes>
          </WorkspaceFicha>
        </CustomOffcanvas>
      </ContainerConteudo>

      <BarraInferiorBotoes>
        <WorkspaceBotoes
          // download
          // downloadDisabled={isExporting || !(data.length > 0)}
          // onClickDownload={handleExport}
          inserir
          onClickInserir={handleShowModal}
        />
      </BarraInferiorBotoes>
    </>
  );
};

export default ContratosIndex;
